// Fleet status (admin only): each customer container against the recorded
// platform (backend lattice_api/platform.yaml) and the permission contract.
// Routes: routes/admin_fleet.py. Every call reads the containers live, so it
// takes seconds: no polling, a 60 s cache and an explicit Refresh.
// What the checks mean: docs/fleet-status.md.
//
// Also the shared bits of the fleet-operations admin routes
// (routes/admin_fleet_ops/*: plan, limits, network, storage, lifecycle,
// upgrades, audit): every one is read-only but the plan record, and each
// refuses with {code, message}, which refusalOf reads back.

import { useQuery } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

type S = components["schemas"];

export type FleetReport = S["FleetReport"];
export type FleetCustomerReport = S["FleetCustomerReport"];
export type FleetRow = S["FleetRow"];
export type FleetResult = FleetRow["result"];
export type FleetLastUpgrade = S["FleetLastUpgrade"];
export type FleetStorageSummary = S["FleetStorageSummary"];

/** A fleet-ops route's refusal: its HTTP status, short code and plain sentence. */
export interface Refusal {
  status: number;
  code: string;
  message: string;
}

/** The {code, message} an admin fleet-ops route refused with; null for any other error. */
export function refusalOf(err: unknown): Refusal | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  const detail = body && typeof body === "object" && "detail" in body ? (body as { detail?: unknown }).detail : null;
  if (!detail || typeof detail !== "object") return null;
  const d = detail as Record<string, unknown>;
  if (typeof d.code !== "string") return null;
  return { status: err.status, code: d.code, message: typeof d.message === "string" ? d.message : d.code };
}

/** The path of an admin route about one customer. */
export const adminCustomerPath = (customer: string, rest: string) =>
  `/api/admin/customers/${encodeURIComponent(customer)}/${rest}`;

export const fleetKeys = {
  all: ["admin-fleet"] as const,
  list: () => [...fleetKeys.all, "list"] as const,
  last: () => [...fleetKeys.all, "last"] as const,
  customer: (c: string) => [...fleetKeys.all, "customer", c] as const,
};

const CACHE = { staleTime: 60_000, refetchOnWindowFocus: false } as const;

export function useAdminFleet() {
  return useQuery({
    queryKey: fleetKeys.list(),
    queryFn: () => apiFetch<FleetReport>("/api/admin/fleet"),
    ...CACHE,
  });
}

/** The last live report the server took (?cached=true): never reads a container, so the
 *  admin home can show fleet failures without probing every workspace on each visit.
 *  `checked_at` null = nobody has opened the fleet since the api started. */
export function useAdminFleetLast() {
  return useQuery({
    queryKey: fleetKeys.last(),
    queryFn: () => apiFetch<FleetReport>("/api/admin/fleet?cached=true"),
    staleTime: 60_000,
  });
}

export function useAdminFleetCustomer(customer: string | null) {
  return useQuery({
    queryKey: fleetKeys.customer(customer ?? ""),
    queryFn: () => apiFetch<FleetCustomerReport>(`/api/admin/fleet/${encodeURIComponent(customer ?? "")}`),
    enabled: Boolean(customer),
    ...CACHE,
  });
}
